import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, Card, Chip, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useModals } from "src/context/ModalContext";
import { JobSelectionForInvoice } from "../../../../../clients/invoice-selection/JobSelectionForInvoice";
import { BaseModalComponent } from "../../../../../common/BaseModalComponent";
import type { JobEventForInvoice } from "../../../../../jobs/jobs-details/events/board/context";
import { alphaVar, colors, jobColors } from "../../../../../ui/colors";
import { getExtraServicesForVisit } from "../../../utils/invoiceExtraLineItems";

interface InvoiceEventsEditorProps {
  invoice: any;
  clientUid?: string;
  isEditable: boolean;
  isSaving: boolean;
  onSave: (payload: any) => void;
  cardStyles?: any;
}

const getJobStatusStyles = (status?: string) => {
  if (!status) return { color: "var(--fx-surface-textsecondary, #64748B)", bg: alphaVar("#475569", 0.12) };

  const key = status.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

  if (key === "scheduled") return jobColors.scheduled;
  if (key === "inprogress" || key === "in_progress") return jobColors.inProgress;
  if (key === "completed") return jobColors.completed;
  if (key === "cancelled") return jobColors.cancelled;
  if (key === "approved") return jobColors.approved;
  if (key === "forrevision" || key === "for_revision") return jobColors.forRevision;
  if (key === "visitjob" || key === "visit_job") return jobColors.visitJob;

  return (
    (jobColors as any)[key] || (jobColors as any)[status] || { color: "var(--fx-surface-textsecondary, #64748B)", bg: alphaVar("#475569", 0.12) }
  );
};

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const InvoiceEventsEditor: React.FC<InvoiceEventsEditorProps> = ({ invoice, clientUid, isEditable, isSaving, onSave, cardStyles = {} }) => {
  const modalKey = `edit-invoice-events-${invoice?.uid || "modal"}`;
  const extraLineItems = (invoice as any)?.pricingDetail?.extraLineItems;

  // Extract initial billed job events
  const initialEvents: any[] = useMemo(() => {
    if (Array.isArray(invoice?.billingJobEvents) && invoice.billingJobEvents.length > 0) {
      return invoice.billingJobEvents;
    }
    if (invoice?.jobEvent) {
      return [invoice.jobEvent];
    }
    return [];
  }, [invoice?.billingJobEvents, invoice?.jobEvent]);

  // Initial event prices map from pricingDetail
  const initialEventPrices = useMemo(() => {
    const prices: Record<string, number> = {};
    const savedLines = (invoice as any)?.pricingDetail?.serviceLines;
    if (Array.isArray(savedLines)) {
      for (const line of savedLines) {
        if (line && line.eventUid && typeof line.lineTotal === "number") {
          prices[line.eventUid] = line.lineTotal;
        }
      }
    }
    return prices;
  }, [invoice?.pricingDetail]);

  const fallbackDefaultPrice = useMemo(() => {
    const serviceTotal = Number(invoice?.mainAreasCost?.total?.value ?? invoice?.totalCost?.total?.value ?? 0);
    if (initialEvents.length > 0 && serviceTotal > 0) {
      return Number((serviceTotal / initialEvents.length).toFixed(2));
    }
    return 0;
  }, [invoice, initialEvents.length]);

  return (
    <Card sx={cardStyles}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700} sx={{ color: colors.gray[700] }}>
            Billed Visits ({initialEvents.length})
          </Typography>

          {isEditable && (
            <BaseModalComponent
              modalKey={modalKey}
              modalLabel="Edit Billed Visits"
              defaultSizes={{
                width: { xs: "96%", sm: "90%", md: 840 },
                height: { xs: "90%", sm: "85%", md: 620 },
              }}
              allowInsidePreventModal
              elementTrigger={
                <Button size="small" startIcon={<EditIcon />}>
                  Edit Visits
                </Button>
              }
            >
              <InvoiceEventsModalContent
                invoice={invoice}
                clientUid={clientUid}
                initialEvents={initialEvents}
                initialEventPrices={initialEventPrices}
                fallbackDefaultPrice={fallbackDefaultPrice}
                isSaving={isSaving}
                onSave={onSave}
                modalKey={modalKey}
              />
            </BaseModalComponent>
          )}
        </Stack>

        {initialEvents.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.gray[500], py: 1, textAlign: "center" }}>
            No billed visits associated with this invoice.
          </Typography>
        ) : (
          <Stack spacing={1.5} divider={<Divider />}>
            {initialEvents.map((event) => {
              const currentPrice = initialEventPrices[event.uid] ?? fallbackDefaultPrice;
              const statusStyle = getJobStatusStyles(event.status);
              const visitExtras = getExtraServicesForVisit(extraLineItems, event.uid, initialEvents.length);
              const visitExtrasTotal = visitExtras.reduce((sum, item) => sum + item.amount, 0);
              const totalVisitPrice = currentPrice + visitExtrasTotal;

              return (
                <Stack key={event.uid} spacing={0.5} sx={{ py: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: colors.gray[500] }} />
                      <Typography variant="body2" fontWeight={600} sx={{ color: colors.gray[700] }}>
                        {formatDateTime(event.startDate)}
                      </Typography>
                      <Chip
                        label={event.status}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: 700,
                          height: 22,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>

                    <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: colors.gray[800] }}>
                        ${totalVisitPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      {visitExtrasTotal > 0 && (
                        <Typography variant="caption" sx={{ color: colors.gray[500], display: "block", fontSize: "0.68rem" }}>
                          Base: ${currentPrice.toFixed(2)} + Extra: ${visitExtrasTotal.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {visitExtras.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
                      {visitExtras.map((extra, eIdx) => (
                        <Chip
                          key={`${extra.id ?? extra.title}-${eIdx}`}
                          label={`+ $${extra.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${extra.title}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            bgcolor: alphaVar(colors.orange[50] ?? "#FFF7ED", 1),
                            color: colors.orange[800] ?? "#9A3412",
                            border: `1px solid ${alphaVar(colors.orange[200] ?? "#FED7AA", 1)}`,
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  <Typography variant="caption" sx={{ color: colors.gray[500] }}>
                    {formatDateTime(event.startDate)} — {formatDateTime(event.endDate)} · Job #{event.jobId || event.job?.id}
                  </Typography>

                  <Typography variant="caption" sx={{ color: colors.gray[400], fontFamily: "monospace", fontSize: "0.6rem" }}>
                    UID: {event.uid}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

interface InvoiceEventsModalContentProps {
  invoice: any;
  clientUid?: string;
  initialEvents: any[];
  initialEventPrices: Record<string, number>;
  fallbackDefaultPrice: number;
  isSaving: boolean;
  onSave: (payload: any) => void;
  modalKey: string;
}

const InvoiceEventsModalContent: React.FC<InvoiceEventsModalContentProps> = ({
  invoice,
  clientUid,
  initialEvents,
  initialEventPrices,
  fallbackDefaultPrice,
  isSaving,
  onSave,
  modalKey,
}) => {
  const { closeByKey } = useModals();
  const extraLineItems = (invoice as any)?.pricingDetail?.extraLineItems;

  const [draftEvents, setDraftEvents] = useState<any[]>(() => [...initialEvents]);
  const [eventPrices, setEventPrices] = useState<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const ev of initialEvents) {
      map[ev.uid] = initialEventPrices[ev.uid] ?? fallbackDefaultPrice;
    }
    return map;
  });

  const targetClientUid =
    clientUid || invoice?.jobEvent?.job?.clientUid || invoice?.jobEvent?.job?.client?.uid || invoice?.quote?.clientUid || invoice?.quote?.client?.uid;

  const jobSelectionModalKey = `ClientJobSelection-${invoice?.uid || "modal"}`;

  const handleReset = () => {
    setDraftEvents([...initialEvents]);
    const map: Record<string, number> = {};
    for (const ev of initialEvents) {
      map[ev.uid] = initialEventPrices[ev.uid] ?? fallbackDefaultPrice;
    }
    setEventPrices(map);
  };

  const handleRemoveEvent = (uid: string) => {
    setDraftEvents((prev) => prev.filter((e) => e.uid !== uid));
  };

  const handlePriceChange = (uid: string, rawVal: string) => {
    const val = Number.parseFloat(rawVal);
    setEventPrices((prev) => ({
      ...prev,
      [uid]: Number.isNaN(val) ? 0 : val,
    }));
  };

  const handleVisitsSelectedFromJobSelection = (selectedEvents: JobEventForInvoice[]) => {
    setDraftEvents((prev) => {
      const existingUids = new Set(prev.map((e) => e.uid));
      const newEvents = selectedEvents.filter((ev) => !existingUids.has(ev.uid));
      return [...prev, ...newEvents];
    });

    setEventPrices((prev) => {
      const updated = { ...prev };
      for (const ev of selectedEvents) {
        if (updated[ev.uid] === undefined) {
          updated[ev.uid] = fallbackDefaultPrice > 0 ? fallbackDefaultPrice : 100;
        }
      }
      return updated;
    });

    closeByKey(jobSelectionModalKey);
  };

  const draftSubtotal = useMemo(() => {
    return draftEvents.reduce((sum, ev) => sum + (eventPrices[ev.uid] ?? fallbackDefaultPrice ?? 0), 0);
  }, [draftEvents, eventPrices, fallbackDefaultPrice]);

  const hasChanges = useMemo(() => {
    if (draftEvents.length !== initialEvents.length) return true;

    for (let i = 0; i < draftEvents.length; i++) {
      if (draftEvents[i].uid !== initialEvents[i]?.uid) return true;
    }

    for (const ev of draftEvents) {
      const currentPrice = eventPrices[ev.uid] ?? fallbackDefaultPrice;
      const origPrice = initialEventPrices[ev.uid] ?? fallbackDefaultPrice;
      if (Math.abs(currentPrice - origPrice) > 0.001) return true;
    }

    return false;
  }, [draftEvents, initialEvents, eventPrices, initialEventPrices, fallbackDefaultPrice]);

  const handleSave = () => {
    const scopeOfWorkUid = invoice?.scopeOfWorkUid || invoice?.jobEvent?.job?.scopeOfWorkUid || draftEvents[0]?.job?.scopeOfWorkUid || null;
    const currency = invoice?.totalCost?.total?.currency || "USD";

    const serviceLines = draftEvents.map((ev) => {
      const price = eventPrices[ev.uid] ?? fallbackDefaultPrice ?? 0;
      return {
        jobId: Number(ev.jobId || ev.job?.id || 0),
        eventUid: ev.uid,
        qty: 1,
        unitPrice: price,
        lineTotal: price,
        isLineTotalOverridden: true,
      };
    });

    const computedTotal = serviceLines.reduce((sum, line) => sum + line.lineTotal, 0);

    const jobEventsPayload = {
      scopeOfWorkUid,
      billingJobEvents: draftEvents.map((ev) => ({ uid: ev.uid })),
      pricingDetail: {
        serviceLine: {
          qty: draftEvents.length,
          unitPrice: draftEvents.length > 0 ? Number((computedTotal / draftEvents.length).toFixed(2)) : 0,
          lineTotal: computedTotal,
          isLineTotalOverridden: true,
        },
        isGlobalTotalOverridden: false,
        computedGlobalTotal: computedTotal,
        serviceLines,
      },
      mainAreasCost: {
        quantity: { value: 1, unit: "per_item" },
        price: { value: computedTotal, by: "unique", currency },
        total: { value: computedTotal, by: "unique", currency },
      },
      otherAreasCost: invoice?.otherAreasCost || {
        quantity: { value: 1, unit: "per_item" },
        price: { value: 0, by: "unique", currency },
        total: { value: 0, by: "unique", currency },
      },
      additionalObjectsCost: invoice?.additionalObjectsCost || {
        quantity: { value: 1, unit: "per_item" },
        price: { value: 0, by: "unique", currency },
        total: { value: 0, by: "unique", currency },
      },
      specialRequestsCost: invoice?.specialRequestsCost || {
        quantity: { value: 1, unit: "per_item" },
        price: { value: 0, by: "unique", currency },
        total: { value: 0, by: "unique", currency },
      },
      increasePercentageByFrequency: invoice?.increasePercentageByFrequency || {
        quantity: { value: 1, unit: "per_item" },
        price: { value: 0, by: "unique", currency },
        total: { value: 0, by: "unique", currency },
      },
      totalCost: {
        quantity: { value: 1, unit: "per_item" },
        price: { value: computedTotal, by: "unique", currency },
        total: { value: computedTotal, by: "unique", currency },
      },
      sendEmail: true,
    };

    onSave({ jobEventsPayload });
    closeByKey(modalKey);
  };

  console.log("draft events: ", draftEvents);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "background.paper" }}>
      {/* Header bar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: alphaVar(colors.orange[50], 0.6),
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ color: colors.gray[800], fontWeight: 800 }}>
            Visits Management ({draftEvents.length})
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray[600] }}>
            Remove visits or add new visits from client jobs. Adjust per-visit prices before saving.
          </Typography>
        </Box>

        {targetClientUid && (
          <BaseModalComponent
            modalKey={jobSelectionModalKey}
            modalLabel="Select Visits to Invoice"
            defaultSizes={{ width: { xs: "95%", md: "740px" }, height: { xs: "90%", md: "80%" } }}
            allowInsidePreventModal
            preventParentTrigger={true}
            elementTrigger={
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                sx={{ bgcolor: colors.orange[500], "&:hover": { bgcolor: colors.orange[600] }, fontWeight: 700 }}
              >
                Add Visits
              </Button>
            }
          >
            <JobSelectionForInvoice clientUid={targetClientUid} onContinue={handleVisitsSelectedFromJobSelection} />
          </BaseModalComponent>
        )}
      </Box>

      {/* Main content list */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.gray[700] }}>
              Selected Visits Breakdown
            </Typography>
            <Typography variant="h6" sx={{ color: colors.orange[700], fontWeight: 800 }}>
              Total: ${draftSubtotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>

          {draftEvents.length === 0 ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: colors.gray[500], mb: 2 }}>
                No visits currently selected for this invoice.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5} divider={<Divider />}>
              {draftEvents.map((event) => {
                const currentPrice = eventPrices[event.uid] ?? fallbackDefaultPrice;
                const visitExtras = getExtraServicesForVisit(extraLineItems, event.uid, draftEvents.length);
                const visitExtrasTotal = visitExtras.reduce((sum, item) => sum + item.amount, 0);
                const statusStyle = getJobStatusStyles(event.status);

                const jobId = event.jobId || event.job?.id;
                const jobAlias = event.job?.alias || "No alias";

                return (
                  <Stack
                    key={event.uid}
                    spacing={1}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: colors.gray[50],
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <CalendarTodayIcon
                          sx={{
                            fontSize: 16,
                            color: colors.gray[500],
                            flexShrink: 0,
                          }}
                        />

                        <Typography variant="body2" fontWeight={700} noWrap sx={{ color: colors.gray[800] }}>
                          {jobAlias}
                        </Typography>

                        <Chip
                          label={event.status}
                          size="small"
                          sx={{
                            textTransform: "capitalize",
                            bgcolor: statusStyle.bg,
                            color: statusStyle.color,
                            fontWeight: 700,
                            height: 22,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>

                      <IconButton size="small" color="error" onClick={() => handleRemoveEvent(event.uid)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            display: "block",
                            color: colors.gray[700],
                          }}
                        >
                          Job #{jobId}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: colors.gray[600],
                          }}
                        >
                          {formatDateTime(event.startDate)} – {formatDateTime(event.endDate)}
                        </Typography>
                      </Box>

                      <TextField
                        size="small"
                        label="Visit Price"
                        value={currentPrice}
                        onChange={(e) => handlePriceChange(event.uid, e.target.value.replace(/[^\d.]/g, ""))}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          inputMode: "decimal",
                        }}
                        helperText={
                          visitExtrasTotal > 0
                            ? `+ $${visitExtrasTotal.toFixed(2)} extra (Total: $${(Number(currentPrice || 0) + visitExtrasTotal).toFixed(2)})`
                            : undefined
                        }
                        sx={{
                          width: visitExtrasTotal > 0 ? 190 : 140,
                          flexShrink: 0,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          "& .MuiInputBase-input": {
                            py: 0.5,
                            fontSize: "0.875rem",
                            textAlign: "right",
                          },
                        }}
                      />
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.gray[400],
                        fontFamily: "monospace",
                        fontSize: "0.6rem",
                      }}
                    >
                      UID: {event.uid}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Footer Controls */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "background.paper",
        }}
      >
        <Button size="medium" color="warning" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={isSaving || !hasChanges}>
          Reset
        </Button>

        <Stack direction="row" spacing={1.5}>
          <Button size="medium" startIcon={<CloseIcon />} onClick={() => closeByKey(modalKey)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isSaving || draftEvents.length === 0 || !hasChanges}
            onClick={handleSave}
            sx={{ bgcolor: colors.orange[500], "&:hover": { bgcolor: colors.orange[600] }, px: 3, fontWeight: 700 }}
          >
            Save Changes
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
